import React, { useEffect } from "react";
import Layout from "./layout";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doFetch, clearSearchResults } from "../redux/api/actions";
import { SEARCH_TOURS } from "../redux/api/constants";

const TourCard = ({ tour }) => {
    const navigate = useNavigate();
    const imageUrls = tour.image_urls ? tour.image_urls.split(",").filter(url => url.trim()) : [];
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleClick = () => {
        navigate(`/tours/${tour.id}`);
    };

    return (
        <div className="search-tour-card" onClick={handleClick}>
            <div className="tour-card-image">
                {imageUrls.length > 0 ? (
                    <img src={imageUrls[0]} alt={tour.title} />
                ) : (
                    <div className="no-image">
                        <i className="bi bi-image"></i>
                    </div>
                )}
            </div>
            <div className="tour-card-content">
                <div className="tour-card-header">
                    <h3>{tour.title}</h3>
                </div>
                
                <p className="tour-card-description">{tour.description}</p>
                
                <div className="tour-card-meta">
                    <span><i className="bi bi-clock"></i> {tour.duration_in_hours} hrs</span>
                </div>

                {tour.available_events && tour.available_events.length > 0 && (
                    <div className="tour-card-availability">
                        <div className="availability-label">
                            <i className="bi bi-calendar-check"></i>
                            Available dates:
                        </div>
                        <div className="availability-dates">
                            {tour.available_events.slice(0, 3).map((event, idx) => (
                                <span key={idx} className="date-chip">
                                    {formatDate(event.tour_date)} at {formatTime(event.tour_date)}
                                </span>
                            ))}
                            {tour.available_events.length > 3 && (
                                <span className="more-dates">+{tour.available_events.length - 3} more</span>
                            )}
                        </div>
                    </div>
                )}

                {tour.tourguide && (
                    <div className="tour-card-host">
                        {tour.tourguide.avatar_url && (
                            <img src={tour.tourguide.avatar_url} alt={tour.tourguide.name} className="host-avatar-small" />
                        )}
                        <span>By {tour.tourguide.name}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const Location = () => {
    const { location } = useParams();
    
    const searchResults = useSelector(state => state.API.searchResults);
    const loading = useSelector(state => state.API.loading);
    const dispatch = useDispatch();

    // Parse location from URL
    const parseLocation = (location) => {
        const locationArray = location.split("--");
        const city = locationArray[0].replace(/-/g, " ");
        let state = "";
        let country = "";
        
        if (locationArray.length === 2) {
            country = locationArray[1].replace(/-/g, " ");
        } else if (locationArray.length === 3) {
            state = locationArray[1] ? locationArray[1].replace(/-/g, " ") : "";
            country = locationArray[2].replace(/-/g, " ");
        }
        
        return { city, state, country };
    };

    const { city, state, country } = parseLocation(location);
    
    // No date/guest filters for audio tours

    useEffect(() => {
        // Build search query
        let query = `locations/search?city=${encodeURIComponent(city)}`;
        if (state) query += `&state=${encodeURIComponent(state)}`;
        if (country) query += `&country=${encodeURIComponent(country)}`;
        query += `&guests=1`;

        dispatch(doFetch(SEARCH_TOURS, query, null));

        return () => {
            dispatch(clearSearchResults());
        };
    }, [dispatch, city, state, country]);

    const locationDisplay = searchResults?.location 
        ? `${searchResults.location.city}${searchResults.location.state ? `, ${searchResults.location.state}` : ''}`
        : `${city}${state ? `, ${state}` : ''}`;

    const countryFlag = searchResults?.location?.flag || '';

    return (
        <Layout selected="location">
            <div className="search-results-page">
                <div className="search-results-header">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item active">{locationDisplay}</li>
                        </ol>
                    </nav>
                    
                    <div className="location-hero">
                        <h1>
                            Experiences in {locationDisplay} {countryFlag}
                        </h1>
                        <p className="results-count">
                            {searchResults?.tours?.length || 0} tours available
                        </p>
                    </div>
                    <div className="search-filters">
                        <div className="text-muted small">
                            Location-based audio tours (no dates or group sizes).
                        </div>
                    </div>
                </div>

                <div className="search-results-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : searchResults?.tours && searchResults.tours.length > 0 ? (
                        <div className="tours-grid-search">
                            {searchResults.tours.map((tour) => (
                                <TourCard key={tour.id} tour={tour} />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-results">
                            <i className="bi bi-compass"></i>
                            <h3>No tours found</h3>
                            <p>
                                There are no tours available in this location yet. Check back soon!
                            </p>
                            <Link to="/tourguide/about" className="btn-primary mt-3">
                                Become a Guide Here
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Location;
